import { initReactI18next } from 'react-i18next';
import i18next from 'i18next';
import { defaultLocale, loadLocale, supportedLocales } from 'virtual:i18n-messages';

/**
 * The admin's translations.
 *
 * ── One chunk per language ─────────────────────────────────────────────────
 *
 * The message files are keyed `key → locale → string`, so adding a string is one
 * edit with every translation in front of you. `vite/plugin-i18n-split.ts`
 * pivots that to locale-major at build time, so the SOURCE keeps the shape that
 * is easy to edit and the BUNDLE gets the shape that is cheap to load — a
 * merchant downloads their own language and none of the other nineteen.
 *
 * ── Why the locale comes from the URL ──────────────────────────────────────
 *
 * Shopify puts `?locale=` on the admin iframe URL and it is the merchant's
 * chosen admin language, which is the thing to match. `navigator.language` is
 * the browser's, and the two disagree constantly — a German merchant working in
 * an English-language Chrome would get an English app while the rest of their
 * admin is German.
 *
 * ⚠ `fr-CA` must resolve to `fr`. Shopify sends region-qualified tags and the
 * message files are keyed by the plain language except where the distinction is
 * real (`pt-BR` vs `pt-PT`, `zh-CN` vs `zh-TW`), so an exact-match-only lookup
 * silently falls back to English for a merchant who has a translation.
 */

export function resolveLocale(raw: string | null | undefined): string {
  if (!raw) return defaultLocale;
  const tag = raw.replace('_', '-');
  const exact = supportedLocales.find((l) => l.toLowerCase() === tag.toLowerCase());
  if (exact) return exact;
  const base = tag.split('-')[0]?.toLowerCase() ?? '';
  const byBase = supportedLocales.find((l) => l.toLowerCase() === base);
  if (byBase) return byBase;
  /**
   * A regional tag with no plain-language file: `pt` alone, or `zh-HK`.
   *
   * Pick the first sibling rather than English. A Hong Kong merchant reading
   * Traditional Chinese is far better served by `zh-TW` than by an English
   * admin, and a Portuguese one by `pt-PT` than by nothing.
   */
  const sibling = supportedLocales.find((l) => l.toLowerCase().startsWith(`${base}-`));
  return sibling ?? defaultLocale;
}

/** The locale Shopify asked for. */
export function requestedLocale(): string {
  const fromUrl = new URLSearchParams(location.search).get('locale');
  return resolveLocale(fromUrl ?? navigator.language);
}

/**
 * Polaris's dictionary shape.
 *
 * Declared here rather than imported: Polaris exports `TranslationDictionary`
 * only from a deep internal path, and the virtual module hands back parsed JSON
 * that has no type of its own.
 */
export type PolarisDictionary = { [key: string]: string | PolarisDictionary };

export interface Loaded {
  locale: string;
  /** Polaris's own dictionary, for its built-in chrome. */
  polaris: PolarisDictionary;
}

/**
 * Load one language and hand it to i18next.
 *
 * ⚠ `fallbackLng` is deliberately `false`, not `'en'`.
 *
 * With an English fallback a missing key renders English inside an otherwise
 * translated page, which looks like a bug and — worse — hides the gap from the
 * build. The plugin already fails the build when a key misses a locale, so a
 * missing key at runtime means something is wrong that should be visible.
 */
export async function initI18n(locale = requestedLocale()): Promise<Loaded> {
  const { messages, polaris } = await loadLocale(locale);

  await i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: false,
    resources: { [locale]: { translation: messages } },
    interpolation: { escapeValue: false },
    // A key that is genuinely absent should read as the key, not as blank space.
    parseMissingKeyHandler: (key: string) => key,
    returnNull: false,
  });

  return { locale, polaris: polaris as PolarisDictionary };
}

export { supportedLocales };
